import { api } from '@/features/auth';

import {
  GetOfferSlotsRequest,
  GetOfferSlotsResponse,
  GetOfferSlotRequest,
  GetOfferMeetingRequest,
  GetOfferMeetingResponse,
  OfferSlot,
  OfferMeeting,
  ProposeSlotsRequest,
  ProposeSlotsResponse,
  ConfirmMeetingRequest,
  ConfirmMeetingResponse,
  BlockedSlot,
  GetBlockedSlotsRequest,
} from './types';

export const calendarApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ===== GET - via items/ (Directus native API) =====

    getOfferSlots: builder.query<OfferSlot[], GetOfferSlotsRequest>({
      query: ({ offer_id }) => ({
        url: 'items/offer_slots',
        method: 'GET',
        params: {
          fields: 'id,offer_id,start_time,end_time,status',
          filter: JSON.stringify({ offer_id: { _eq: offer_id } }),
          sort: 'start_time',
        },
      }),
      transformResponse: ({ data }: GetOfferSlotsResponse) => data,
      providesTags: (_result, _error, args) => [{ type: 'OfferSlots', id: args.offer_id }],
    }),

    getOfferSlot: builder.query<OfferSlot, GetOfferSlotRequest>({
      query: ({ slot_id }) => ({
        url: `items/offer_slots/${slot_id}`,
        method: 'GET',
        params: {
          fields: 'id,offer_id,start_time,end_time,status',
        },
      }),
      transformResponse: ({ data }: { data: OfferSlot }) => data,
      providesTags: (_result, _error, args) => [{ type: 'OfferSlots', id: args.slot_id }],
    }),

    getOfferMeeting: builder.query<OfferMeeting | null, GetOfferMeetingRequest>({
      query: ({ offer_id }) => ({
        url: 'items/offer_meetings',
        method: 'GET',
        params: {
          fields:
            'id,offer_id,selected_slot_id,meeting_status,google_calendar_event_id,google_meet_link,meeting_confirmed_at',
          filter: JSON.stringify({ offer_id: { _eq: offer_id } }),
          limit: 1,
        },
      }),
      transformResponse: ({ data }: GetOfferMeetingResponse) => data?.[0] ?? null,
      providesTags: (_result, _error, args) => [{ type: 'OfferMeetings', id: args.offer_id }],
    }),

    getBlockedSlots: builder.query<BlockedSlot[], GetBlockedSlotsRequest>({
      query: ({ date_from, date_to, opportunity_id }) => {
        const filter: any = {
          _and: [
            { date: { _gte: date_from } },
            { date: { _lte: date_to } },
            {
              _or: [
                { opportunity_id: { _null: true } },
                ...(opportunity_id ? [{ opportunity_id: { _eq: opportunity_id } }] : []),
              ],
            },
          ],
        };

        return {
          url: 'items/blocked_slots',
          method: 'GET',
          params: {
            fields: 'id,date,all_day,start_time,end_time,reason,opportunity_id',
            filter: JSON.stringify(filter),
            sort: 'date,start_time',
          },
        };
      },
      transformResponse: ({ data }: { data: BlockedSlot[] }) => data,
      providesTags: ['BlockedSlots'],
    }),

    // ===== POST - via /calendar/ (custom extension) =====
    proposeSlots: builder.mutation<ProposeSlotsResponse, ProposeSlotsRequest>({
      query: (body) => ({
        url: 'calendar/offers/propose-slots',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'OfferSlots', id: args.offer_id },
        { type: 'OfferMeetings', id: args.offer_id },
        { type: 'Offers', id: args.offer_id },
      ],
    }),

    confirmMeeting: builder.mutation<ConfirmMeetingResponse, ConfirmMeetingRequest>({
      query: (body) => ({
        url: 'calendar/offers/confirm-meeting',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'OfferSlots', id: args.offer_id },
        { type: 'OfferMeetings', id: args.offer_id },
      ],
    }),
  }),
});

export const {
  useGetOfferSlotsQuery,
  useGetOfferSlotQuery,
  useGetOfferMeetingQuery,
  useGetBlockedSlotsQuery,
  useProposeSlotsMutation,
  useConfirmMeetingMutation,
} = calendarApi;
